/**
 * Driver run hooks (docs/mobile-design.md Phase 3, chunk 1): the run, with the offline outbox's waiting actions laid
 * over it, and the stop actions, which always go through the outbox (saved on the phone first, sent when there is
 * signal). No DOM: the same hooks serve React DOM and React Native.
 */
import { useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { applyPendingRunOps, podNote, type FailedReason, type RunView } from '@tradeoutlet/shared';
import { dataApi } from './client';
import { qk } from './keys';
import { toPendingRunOp, type RunOutbox, type RunOutboxOp } from './runOutbox';

type Ws = string | null | undefined;

let outbox: RunOutbox | null = null;
/** The app hands over its outbox once at startup (web: lib/runOutbox.ts). */
export function configureRunOutbox(o: RunOutbox | null) { outbox = o; }
export function runOutbox(): RunOutbox {
  if (!outbox) throw new Error('@tradeoutlet/data: call configureRunOutbox first');
  return outbox;
}

export const runKey = (ws: Ws, id: string) => qk.fleet(ws ?? null, 'run', id);

const EMPTY: RunOutboxOp[] = [];
/** Every waiting run op (all runs), re-rendering on change. */
export function useRunOutboxOps(): RunOutboxOp[] {
  return useSyncExternalStore(
    fn => (outbox ? outbox.subscribe(fn) : () => undefined),
    () => (outbox ? outbox.ops() : EMPTY),
    () => EMPTY,
  );
}

/** A logged-in driver's run with the waiting actions applied (shown done, marked waiting). */
export function useDriverRun(ws: Ws, id: string | null | undefined) {
  const q = useQuery({
    queryKey: runKey(ws, id ?? ''), queryFn: () => dataApi().fleet.run({ ws: ws!, id: id! }), enabled: Boolean(ws && id),
    refetchInterval: 60_000, retry: 1, networkMode: 'offlineFirst',
  });
  const ops = useRunOutboxOps();
  const mine = useMemo(() => ops.filter(o => o.journeyId === id), [ops, id]);
  const overlay = useMemo(() => (q.data ? applyPendingRunOps(q.data, mine.map(toPendingRunOp)) : null), [q.data, mine]);
  return { query: q, view: overlay?.view ?? null, waitingStopIds: overlay?.waitingStopIds ?? new Set<string>(), startWaiting: overlay?.startWaiting ?? false, ops: mine };
}

export interface PodInput {
  failed: boolean;
  reason?: FailedReason | null;
  name: string;
  note: string;
  pallets: number | null;
  signature?: string | null; // PNG data URL
  photos: string[]; // JPEG data URLs
}

/** The stop actions; each is queued (with its Idempotency-Key) and sent straight away when online. */
export function runActions(ws: string, journeyId: string) {
  const o = runOutbox();
  return {
    start: (tracking_consent: boolean, label = 'Run started') => o.enqueue({ kind: 'run.start', workspaceId: ws, journeyId, label, payload: { tracking_consent } }),
    arrive: (stopId: string, label = 'Arrived') => o.enqueue({ kind: 'run.arrive', workspaceId: ws, journeyId, stopId, label, payload: {} }),
    complete: (stopId: string, p: PodInput, label = p.failed ? 'Not delivered' : 'Delivered') => o.enqueue({
      kind: 'run.complete', workspaceId: ws, journeyId, stopId, label,
      payload: {
        pod: { failed: p.failed, pod_note: podNote(p), pod_name: p.failed ? null : p.name.trim() || null, pod_pallets: p.failed ? null : p.pallets },
        signature: !p.failed && p.signature ? { key: '', data_url: p.signature } : null,
        photos: p.photos.map(d => ({ key: '', data_url: d })),
      },
    }),
  };
}

/** Cache wiring for the app's outbox: a sent op's answer replaces the cached run; "already done" refetches. */
export function applyRunSent(qc: { setQueryData: (k: readonly unknown[], v: RunView) => unknown; invalidateQueries: (f: { queryKey: readonly unknown[] }) => unknown }, op: RunOutboxOp, view: RunView | null) {
  if (view) qc.setQueryData(runKey(op.workspaceId, op.journeyId), view);
  else void qc.invalidateQueries({ queryKey: runKey(op.workspaceId, op.journeyId) });
  void qc.invalidateQueries({ queryKey: qk.fleet(op.workspaceId, 'journeys') });
}
